import { useState } from "react";
import { styled } from "@mui/material/styles";

// Interfaces
import { IPartialRecalculateWashoutInput, IRecalculateWashoutsParams } from "@/Washouts/types/Api/commons";

// API's
import { recalculateWashout } from "@/Washouts/api/common/mutations";

import { resolveWashoutButtonStyles } from "@/Washouts/helpers/WashoutReview";
import { Dialog, IconButton, Tooltip, Typography, DialogTitle, DialogContent } from "@mui/material";
import { ReactComponent as WashoutFile } from "@/Washouts/assets/icons/washoutFile.svg";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQueries } from "@tanstack/react-query";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
import { IWashoutQueries } from "@/Washouts/types/WashoutReview";
import { isMutablePosition, normalizePositions } from "@/Common/helpers/Positions";
import RenderWashoutInformation from "../RenderWashoutInformation";
import { getMappedPosition } from "@/Common/api/positions/queries";

type TWashoutDialogProps = {
	openPopper: boolean;
	alertMessage: string;
	attachedFilesLength: number;
	userId: string;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	position: string;
	canEditWashout: boolean;
};

const BootstrapDialog = styled(Dialog)(() => ({
	"& .MuiPaper-root": {
		borderRadius: "10px",
	},
}));

const WashoutDialog = (props: TWashoutDialogProps) => {
	const {
		openPopper,
		alertMessage,
		attachedFilesLength,
		userId,
		locationId,
		commissionCycleId,
		payPeriodId,
		canEditWashout,
		position: selectedPosition,
	} = props;
	const [openDialog, setOpenDialog] = useState(false);

	const { useWashoutQueries } = useFactoryWashout();
	const position = normalizePositions(selectedPosition);
	const washoutQueries = useWashoutQueries(position) as unknown as IWashoutQueries;

	const [{ data: washoutData, isLoading: isLoadingWashout }, { data: positionType, isLoading: isLoadingPositionType }] = useQueries({
		queries: [
			{
				queryKey: ["getWashout", payPeriodId, selectedPosition, userId, commissionCycleId],
				queryFn: async () => washoutQueries?.getWashout?.(locationId, userId, commissionCycleId, payPeriodId, selectedPosition),
				enabled: !!locationId && !!userId && !!commissionCycleId && openDialog,
				staleTime: Infinity,
			},
			{
				queryKey: ["positionType", locationId, position],
				queryFn: async () => {
					const isMutable = isMutablePosition(position);
					if (isMutable) {
						const mappedPosition = await getMappedPosition(locationId, position);
						return mappedPosition?.isDepartmental ? "Departmental" : "Producer";
					}
					return "Producer";
				},
				enabled: !!locationId && !!position,
				staleTime: Infinity,
			},
		],
	});

	const { mutate: recalculate, isPending: isPendingSummaryChange } = useMutation({
		mutationKey: ["recalculateWashout"],
		mutationFn: (params: IRecalculateWashoutsParams) => recalculateWashout(params),
	});

	const handleSummaryChange = (input: IPartialRecalculateWashoutInput) => {
		if (input && locationId) {
			const params = {
				input,
				locationId,
				position: washoutData?.employee?.position ?? position,
				isDepartmental: Boolean(positionType === "Departmental"),
				washoutId: washoutData?._id ?? "",
			};
			recalculate(params);
		}
	};

	return (
		<>
			<Tooltip title={<Typography className="!font-sans">Open Washout</Typography>} placement="top" arrow>
				<IconButton
					sx={resolveWashoutButtonStyles({ openPopper, alertMessage, attachedFilesLength })}
					onClick={() => setOpenDialog(true)}
				>
					<WashoutFile fontSize="inherit" />
				</IconButton>
			</Tooltip>
			<BootstrapDialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xl">
				<DialogTitle sx={{ mb: 3, p: 2 }} id="customized-dialog-title" />
				<IconButton
					aria-label="close"
					onClick={() => setOpenDialog(false)}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent sx={{ width: "1410px", padding: 0 }} dividers>
					<RenderWashoutInformation
						position={position}
						positionType={positionType}
						properties={{
							handleSummaryChange,
							overrideCompensation: canEditWashout,
							isLoading: isLoadingWashout || isLoadingPositionType || isPendingSummaryChange,
							editable: canEditWashout,
							model: washoutData,
							hideEmployeeMeta: false,
							disableBoxShadow: true,
							employeeMetaProps: {
								hiddenReportButton: true,
								containerProps: {
									roundedTop: false,
								},
							},
						}}
					/>
				</DialogContent>
			</BootstrapDialog>
		</>
	);
};

export default WashoutDialog;
